﻿// RVR (Runway Visual Range) values are transmitted as hundreds of feet (e.g. 24 == 2,400ft),
// with two reserved sentinel codes used by the feed.
export const RVR_NO_DATA = 99;
export const RVR_NO_REPORT = 88;

export type RvrSegment = 'TD' | 'MP' | 'RO';

export function isRvrValueValid(value: number | null | undefined): value is number {
    return value != null && value !== RVR_NO_DATA && value !== RVR_NO_REPORT && value > 0;
}

export function getRvrColor(value: number | null | undefined): string | null {
    if (!isRvrValueValid(value)) {
        return null;
    }
    if (value >= 60) {
        return '#00ff00';
    }
    if (value >= 24) {
        return 'rgb(0, 120, 250)';
    }
    if (value >= 12) {
        return '#ffff00';
    }
    if (value >= 6) {
        return '#ff8000';
    }
    if (value >= 3) {
        return '#ff0000';
    }
    return '#ff00ff';
}

export function formatRvrValue(value: number | null | undefined): string {
    if (value == null) {
        return '';
    }
    if (value === RVR_NO_REPORT) {
        return 'No Report';
    }
    if (value === RVR_NO_DATA) {
        return 'No Data';
    }
    return `${(value * 100).toLocaleString('en-US')} ft`;
}

export function runwayIdHeadingDeg(runwayId: string | null | undefined): number | null {
    if (!runwayId) {
        return null;
    }
    const match = runwayId.trim().match(/^(\d{1,2})/);
    if (!match) {
        return null;
    }
    return (parseInt(match[1], 10) * 10) % 360;
}

function angularDiff(a: number, b: number): number {
    let diff = Math.abs(a - b) % 360;
    if (diff > 180) {
        diff = 360 - diff;
    }
    return diff;
}

export interface RunwayEndAssignment {
    baseEnd: 'endA' | 'endB';
    recipEnd: 'endA' | 'endB';
}

export function assignRunwayEnds(bearingDeg: number, baseId: string | null, recipId: string | null): RunwayEndAssignment | null {
    const baseHeading = runwayIdHeadingDeg(baseId);
    const recipHeading = runwayIdHeadingDeg(recipId);
    if (baseHeading == null || recipHeading == null) {
        return null;
    }

    const diffToBase = angularDiff(bearingDeg, baseHeading);
    const diffToRecip = angularDiff(bearingDeg, recipHeading);
    const endAIsBase = diffToBase <= diffToRecip;

    return endAIsBase ? { baseEnd: 'endA', recipEnd: 'endB' } : { baseEnd: 'endB', recipEnd: 'endA' };
}
